
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CreditCard, PlusCircle, ArrowRight, Upload, ArrowLeft, Save } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import ContasBancariasLista from "../contas/ContasBancariasLista";
import CadastroContaBancaria from "../contas/CadastroContaBancaria";
import ExtratosBancarios from "../contas/ExtratosBancarios";
import ImportacaoExtrato from "../contas/ImportacaoExtrato";

interface ContasTabContentProps {
  showCadastroForm?: boolean;
  onFormVisibilityChange?: (visibility: boolean) => void;
}

const ContasTabContent: React.FC<ContasTabContentProps> = ({ 
  showCadastroForm: externalShowCadastroForm = false,
  onFormVisibilityChange
}) => {
  const [showCadastroForm, setShowCadastroForm] = useState(externalShowCadastroForm);
  const [activeTab, setActiveTab] = useState("contas");
  const [showImportacaoForm, setShowImportacaoForm] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Sync with external form visibility state
  useEffect(() => {
    setShowCadastroForm(externalShowCadastroForm);
  }, [externalShowCadastroForm]);

  const handleAddConta = () => {
    setShowCadastroForm(true);
    if (onFormVisibilityChange) {
      onFormVisibilityChange(true);
    }
  };

  const handleImportarExtrato = () => {
    setShowImportacaoForm(true);
  };

  const handleCloseForms = () => {
    setShowCadastroForm(false);
    setShowImportacaoForm(false);
    if (onFormVisibilityChange) {
      onFormVisibilityChange(false);
    }
  };

  const handleSalvar = () => {
    toast({
      title: "Dados salvos",
      description: "As informações foram salvas com sucesso",
      duration: 3000,
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-600"
            >
              <ArrowLeft className="h-4 w-4 mr-1 text-blue-400" />
              <span className="text-xs">Voltar</span>
            </Button>
            <div>
              <h2 className="text-xl font-normal text-gray-700">Contas e Extratos</h2>
              <p className="text-gray-500 mt-1 text-xs">Gerencie suas contas bancárias e extratos</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            <Button 
              onClick={handleSalvar}
              className="bg-blue-500 hover:bg-blue-600 text-white text-xs"
            >
              <Save className="h-4 w-4 mr-1" /> Salvar
            </Button>
            <Button 
              onClick={handleAddConta}
              className="bg-blue-500 hover:bg-blue-600 text-white text-xs"
            >
              <PlusCircle className="h-4 w-4 mr-1" /> Nova Conta
            </Button>
            <Button 
              variant="outline" 
              onClick={handleImportarExtrato}
              className="border-gray-200 text-gray-600 text-xs"
            >
              <Upload className="h-4 w-4 mr-1 text-blue-400" /> Importar Extrato
            </Button>
          </div>
        </div>

        {/* Tabs for different views */}
        <Tabs defaultValue="contas" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 border-b border-gray-200">
            <TabsTrigger 
              value="contas" 
              className={`px-4 py-2 border-b-2 transition-colors text-xs ${
                activeTab === "contas" 
                  ? "border-blue-500 text-blue-500" 
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Contas Bancárias
            </TabsTrigger>
            <TabsTrigger 
              value="extratos" 
              className={`px-4 py-2 border-b-2 transition-colors text-xs ${
                activeTab === "extratos" 
                  ? "border-blue-500 text-blue-500" 
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Extratos
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="contas">
            {showCadastroForm ? (
              <CadastroContaBancaria onClose={handleCloseForms} />
            ) : (
              <ContasBancariasLista onAddConta={handleAddConta} />
            )}
          </TabsContent>
          
          <TabsContent value="extratos">
            {showImportacaoForm ? (
              <ImportacaoExtrato onClose={handleCloseForms} />
            ) : (
              <ExtratosBancarios onImportarExtrato={handleImportarExtrato} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ContasTabContent;
